import { IDictionaryData } from '@models/dictionary/IDictionaryData';
import { IGridModalAttribute, IGridModalAttributes } from '@models/Forms/IForms';
import { dictAttrField, dictJoinedValue } from './DictpickerModal/ModeControls/dictGridColumns';

/** Строка справочника в выпадающем списке. */
export interface IDictRowView {
    /** Код слева; нет — код не показываем (не виден в шаблоне или совпадает с названием). */
    code?: string;
    title: string;
    /** Вторая строка: значения колонок через « · ». */
    subtitle?: string;
}

export interface IDictRowLayout {
    view: (row: IDictionaryData) => IDictRowView;
    /** Поля серверного поиска (`code`, `fields[i].value`): код строки и колонки, видные в строке. */
    searchFields: string[];
    /** Поле названия строки — порядок списка, когда шаблон его не задаёт. */
    titleField?: string;
}

const SUBTITLE_LIMIT = 3;
const UNTITLED = 'Элемент без названия';
const NAME_ATTRIBUTE = 'Наименование';

const formatValue = (value: unknown, type: IGridModalAttribute['type']): string => {
    if (value === null || value === undefined) return '';
    const text = String(value).trim();
    if (!text) return '';
    if (type === 'date' || type === 'datetime') {
        const date = new Date(text);
        if (!Number.isNaN(date.getTime())) {
            return type === 'date' ? date.toLocaleDateString('ru-RU') : date.toLocaleString('ru-RU');
        }
    }
    return text;
};

/** Значение колонки шаблона в строке: код, атрибут по имени или колонка присоединённого справочника. */
const attrValue = (row: IDictionaryData, attr: IGridModalAttribute): string => {
    if (attr.joined) return formatValue(dictJoinedValue(row, attr), attr.type);
    if (attr.key === 'code') return formatValue(row.code, attr.type);
    return formatValue(row.fields?.find((f) => f.name === attr.key)?.value, attr.type);
};

/** Поле серверного поиска по колонке; у присоединённых и неизвестных схеме колонок поиска нет. */
const searchField = (attr: IGridModalAttribute | undefined, dictAttributes: string[]) =>
    attr && !attr.joined ? dictAttrField(attr.key, dictAttributes) : undefined;

const unique = (fields: (string | undefined)[]) => [...new Set(fields.filter((f): f is string => !!f))];

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// служебный GUID человеку ничего не говорит, а повтор названия — шум
const withoutUselessCode = (view: IDictRowView): IDictRowView =>
    view.code && (view.code === view.title || GUID.test(view.code)) ? { ...view, code: undefined } : view;

/**
 * Как показать строку справочника в выпадающем списке.
 *
 * Только явно — атрибут `dropdown` у колонок `<gridAttribute>`: `code`, `title`, `subtitle` (до трёх), `none`.
 * Без разметки строка — как в прежнем select-режиме: «Наименование», иначе код строки. По колонкам шаблона
 * не угадываем: в `code` часто лежит само название, а соседние колонки бывают служебными («Код Bidzaar» 0/1).
 */
export function buildDictRowLayout(
    gridAttribute: IGridModalAttributes | undefined,
    dictAttributes: string[],
): IDictRowLayout {
    const attrs = gridAttribute?.attrs ?? [];

    if (attrs.some((attr) => attr.dropdown)) {
        const codeAttr = attrs.find((attr) => attr.dropdown === 'code');
        const titleAttr = attrs.find((attr) => attr.dropdown === 'title');
        const subtitleAttrs = attrs.filter((attr) => attr.dropdown === 'subtitle').slice(0, SUBTITLE_LIMIT);
        return {
            view: (row) => {
                const code = codeAttr ? attrValue(row, codeAttr) : undefined;
                const title = (titleAttr && attrValue(row, titleAttr)) || code || row.code || UNTITLED;
                const subtitle = subtitleAttrs
                    .map((attr) => attrValue(row, attr))
                    .filter(Boolean)
                    .join(' · ');
                return withoutUselessCode({ code: code || undefined, title, subtitle: subtitle || undefined });
            },
            // код строки сервер ищет всегда: вставил код — нашёл значение, даже если код не показан
            searchFields: unique([
                'code',
                ...[codeAttr, titleAttr, ...subtitleAttrs].map((a) => searchField(a, dictAttributes)),
            ]),
            titleField: searchField(titleAttr, dictAttributes),
        };
    }

    return {
        view: (row) => {
            // у псевдосправочника уровня закупки «Код» — номер уровня, а название лежит в коде строки
            const named = row.fields?.find((field) => field.name === NAME_ATTRIBUTE)?.value;
            return { title: String(named ?? '').trim() || row.code || UNTITLED };
        },
        searchFields: unique(['code', dictAttrField(NAME_ATTRIBUTE, dictAttributes)]),
        titleField: dictAttrField(NAME_ATTRIBUTE, dictAttributes),
    };
}

/** Совпадение строки с поиском на клиенте — по тому, что видно в строке. */
export const dictRowMatches = (view: IDictRowView, query: string) => {
    const needle = query.toLowerCase();
    if (!needle) return true;
    return [view.code, view.title, view.subtitle].some((text) => text?.toLowerCase().includes(needle));
};
